import React, { useReducer, useEffect, useMemo, Suspense, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"

import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs"
import { StatsCard } from "../../components/cards/stats-card"
import useTranslation from "../../components/CustomHooks/useTranslation"
import type { TableDetailsItem } from "../../components/tables/tableFunctions"
import type { RootState } from "../../store"
import { adminContents } from "../AdminManagement/components/AdminManagementContent"
import endpoints from "../../services/endpoints"
import type { DynamicFilterConfig } from "../../components/DynamicComponent/DynamicFilterPanel"

// Lazy load MainTable for performance
const GridTable = React.lazy(() => import("../../components/GridTable/GridTable"))

// --------------------
// Interfaces
// --------------------
interface TicketData {
  id: string
  ticket_id: string
  user_id: string
  user_type: string
  email: string
  full_name: string
  reason: string
  description: string
  document: string | null
  recording: string | null
  status: string
  created_date: string
  created_at: string
  updated_at: string
}

interface TicketSummary {
  total: number
  open: number
  closed: number
  in_progress: number
}

// --------------------
// Reducer Setup
// --------------------
type TicketSummaryAction =
  | { type: "SET_SUMMARY"; payload: TicketSummary }
  | { type: "RESET" }

const ticketSummaryReducer = (state: TicketSummary, action: TicketSummaryAction): TicketSummary => {
  switch (action.type) {
    case "SET_SUMMARY":
      return { ...state, ...action.payload }
    case "RESET":
      return { total: 0, open: 0, closed: 0, in_progress: 0 }
    default:
      return state
  }
}

// --------------------
// Static Table Configuration
// --------------------
const TableDetails: { [key: string]: TableDetailsItem } = {
  "Ticket ID": { id: "ticket_id", className: "min-w-[120px]" },
  "Created Date and Time": { id: "created_at", className: "min-w-[120px]", custom: ["created_at"], sort: true },
  "Ticket Type": { id: "reason", className: "min-w-[120px]", custom: ["reason"] },
  "Raised By": { id: "raised_by", className: "min-w-[120px]", custom: ["raised_by"] },
  "Assignee": { id: "assignee", className: "min-w-[120px]", custom: ["assignee"] },
  "Location": { id: "location", className: "min-w-[120px]", custom: ["ticket_location"] },
  "Channel": { id: "channel", className: "min-w-[120px]", custom: ["channel"] },
  "Priority": { id: "priority", className: "min-w-[100px]", custom: ["priority"] },
  Status: { id: "status", className: "min-w-[100px]", custom: ["status"] },

  Actions: { id: "actions", className: "min-w-[80px]", actionTypes: ["view"] },
}


// --------------------
// Dynamic Filter Configuration
// --------------------
const dynamicFilterConfig: DynamicFilterConfig = {
  "Date Range": {
    label: "Date Range",
    type: "date",
    required: false,
    searchKey: {
      start: "start_date",
      end: "end_date",
    },
  },
  Assignee: {
    label: "Assignee",
    type: "radio",
    options: ["All", "Assigned", "Unassigned"],
    default: "All",
    required: false,
    searchKey: "assignee_status",
  },
  Status: {
    label: "Status",
    type: "radio",
    options: ["All", "Open", "Closed", "In Progress"],
    default: "All",
    required: false,
    searchKey: "status",
  },
  Location: {
    label: "Location",
    type: "dropdown",
    options: [], // we fill these dynamically
    required: false,
    searchKey: "", // not used because of subFilters
    subFilters: {
      country: {
        label: "Country",
        placeholder: "Select country",
        searchKey: "country",
      },
      region: {
        label: "Region",
        placeholder: "Select region",
        searchKey: "region",
      },
      province: {
        label: "Province",
        placeholder: "Select province",
        searchKey: "province",
      },
    },
  },
}

// --------------------
// Ticket Summary Cards Component
// --------------------
const TicketSummaryCards = ({
  summary,
  isLoading,
}: {
  summary: TicketSummary
  isLoading: boolean
}) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 px-">
    <StatsCard
      icon="total-ticket"
      label="Total Tickets Raised"
      value={String(summary.total)}
      loading={isLoading}
      className="p-3 sm:p-4"
    />
    <StatsCard
      icon="tickets-open"
      label="Tickets Open"
      value={String(summary.open)}
      loading={isLoading}
      className="p-3 sm:p-4"
    />
    <StatsCard
      icon="tickets-closed"
      label="Tickets Closed"
      value={String(summary.closed)}
      loading={isLoading}
      className="p-3 sm:p-4"
    />
    <StatsCard
      icon="tickets-in-progress"
      label="Tickets In Progress"
      value={String(summary.in_progress)}
      loading={isLoading}
      className="p-3 sm:p-4"
    />
  </div>
)

// --------------------
// Component
// --------------------
const ListTickets: React.FC = () => {
  const navigate = useNavigate()
  const { translate } = useTranslation(adminContents)
  const initialParams = useMemo(() => ({ page_number: "1" }), []); // still correct
  const [is1024x768, setIs1024x768] = useState(
    window.innerWidth === 1024 && window.innerHeight === 768
  )


    useEffect(() => {
    function handleResize() {
      const match = window.innerWidth === 1024 && window.innerHeight === 768
      setIs1024x768(match)
    }

    handleResize()

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])


  const entireResponse = useSelector((state: RootState) => state.list.entireResponse)

  // Ticket summary state using useReducer
  const [ticketSummary, dispatchTicketSummary] = useReducer(ticketSummaryReducer, {
    total: 0,
    open: 0,
    closed: 0,
    in_progress: 0,
  })

  // Memoized static text
  const staticText = useMemo(
    () => ({
      emptyScreenText: {
        heading1: translate("There are no tickets raised yet"),
        heading2: translate("Click “Raise Ticket” to raise one"),
      },
    }),
    [translate],
  )

  // Endpoint
  const listTicketsEndpoint = endpoints.listTickets

  // Loading state from redux
  const isLoading = useSelector((state: RootState) => state.list.loading)

  // --------------------
  // Handlers
  // --------------------
  const handleActions = (id: string, action: string, data?: any) => {
    if (action === "view" && data) {
      navigate(`/support/list-tickets/view-ticket/${data.id}`)
    }
  }

  // const addButtonClick = () => {
  //   navigate("/support/raise-ticket")
  // }

  // --------------------
  // Effects
  // --------------------
  useEffect(() => {
    if (
      entireResponse?.summary &&
      JSON.stringify(entireResponse.summary) !== JSON.stringify(ticketSummary)
    ) {
      dispatchTicketSummary({ type: "SET_SUMMARY", payload: entireResponse.summary })
    }
  }, [entireResponse?.summary])
  

  // --------------------
  // Render
  // --------------------
  return (
    <div>
      {/* Breadcrumbs and Raise Ticket Button */}
      <Breadcrumbs
        navPath={["Support", "Raised Tickets"]}
        heading="Raised Tickets"
        noNavigation
        links={["/support/list-tickets", "/support/list-tickets"]}
        // button={{ buttonName: "Raise Ticket", className: "w-[130px]", buttonOnclick: addButtonClick }}
        list={true}
      />

      <div className="bg-white mt-1.5 rounded-lg h-[calc(100vh-160px)] sm:h-[calc(100vh-220px)] overflow-y-auto scrollBar">
        {/* Ticket Summary Cards */}
        <div className="mb-4">
          <h1 className="text-heading4 text-primary_text p-4 ml-3">Ticket Summary</h1>
          <TicketSummaryCards summary={ticketSummary} isLoading={isLoading} />
        </div>

        {/* Tickets Table */}
        <div className="mx-3">
          <span className="text-primary_text text-heading4">
            List of all Raised Support Tickets
          </span>

          <Suspense fallback={<div>Loading table...</div>}>
<GridTable
  staticText={staticText}
  mainEndPoint={listTicketsEndpoint}
  TableDetails={TableDetails}
  handleActions={handleActions}
  params={initialParams}
  searchPlaceholder="Search by ticket id"
  filters={true}
  exporting={false}
  hasActivateDeactivateMDPrivilege={false}
  ticketList={true}
  heightClassName={`
    ${is1024x768 ? "h-[calc(100vh-620px)]" : "h-[calc(100vh-470px)] sm:h-[calc(100vh-570px)]"}
  `}
  dynamicFilterConfig={dynamicFilterConfig}
  hasViewMDPrivilege={true}
  mainTableHeight=" "
  customHeight="h-[220px]"
/>
          </Suspense>
        </div>
      </div>
    </div>
  )
}

export default ListTickets